import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import tz from 'dayjs/plugin/timezone';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { Wrapper } from 'react-aria-tabpanel';
import { useRouter } from 'next/router';

import GradientButton from 'react-scale/components/pages/transformx/common/gradient-button';
import Paginator from 'react-scale/components/elements/paginator';
import {
  ResourceCard,
  BlogCard,
  CustomerCard,
  DatasetCard,
  InterviewCard,
  EventCard
} from 'react-scale/components/pages/resources/ResourcesList/box';

import ResourcesFilters, { FilterBox, FilterItem, FilterName } from '../resources-filters';

import styles from './ResourcesList.module.css';

dayjs.extend(utc);
dayjs.extend(tz);
dayjs.extend(advancedFormat);

const PAGE_SIZE = 9;

const ResourcesList = ({
  blogs,
  events,
  customers,
  resources,
  interviews,
  datasets,
  featuredResources = []
}) => {
  const router = useRouter();
  const { page: pageQuery } = router.query;
  const [mobileItems, setMobileItems] = useState(PAGE_SIZE);

  const getAllFilteredItems = (filters = {}) => {
    const resourcesItems = resources.map(post => (
      <ResourceCard content={post} key={post.id} date={post.createdAt} />
    ));
    const blogsItems = blogs.map(post => (
      <BlogCard content={post} key={post.id} date={post.date} />
    ));
    const customersItems = customers.map(customer => (
      <CustomerCard content={customer} key={customer.id} date={customer.createdAt} />
    ));
    const datasetsItems = datasets.map(dataset => (
      <DatasetCard content={dataset} key={dataset.slug} />
    ));
    const interviewsItems = interviews.map(interview => (
      <InterviewCard content={interview} key={interview.id} date={interview.publishedDate} />
    ));
    const eventsItems = events
      .filter(event => event.slug || event.link !== '')
      .map(event => <EventCard content={event} key={event.id} date={event.date} />);

    if (
      (filters.type?.length < 1 || filters.type[0] === '') &&
      (filters.product?.length < 1 || filters.product[0] === '') &&
      (filters.industry?.length < 1 || filters.industry[0] === '') &&
      (filters.useCase?.length < 1 || filters.useCase[0] === '')
    ) {
      return [
        ...resourcesItems,
        ...blogsItems,
        ...customersItems,
        ...eventsItems,
        ...interviewsItems,
        ...datasetsItems
      ];
    }

    const isIncludedOnFilters = ({ props }) => {
      let included = true;

      if (
        filters.product.length > 0 &&
        !filters.product?.some(product =>
          props.content.filterType?.some(filter => filter.filter?.value?.data.title === product)
        )
      )
        included = false;
      else if (
        filters.industry.length > 0 &&
        !filters.industry?.some(product =>
          props.content.filterType?.some(filter => filter.filter?.value?.data.title === product)
        )
      )
        included = false;
      else if (
        filters.useCase.length > 0 &&
        !filters.useCase?.some(product =>
          props.content.filterType?.some(filter => filter.filter?.value?.data.title === product)
        )
      )
        included = false;

      return included;
    };

    const items = [];

    if (filters.type.includes('resources') || filters.type.length < 1)
      items.push(...resourcesItems.filter(isIncludedOnFilters));
    if (filters.type.includes('blogs') || filters.type.length < 1)
      items.push(...blogsItems.filter(isIncludedOnFilters));
    if (filters.type.includes('caseStudies') || filters.type.length < 1)
      items.push(...customersItems.filter(isIncludedOnFilters));
    if (filters.type.includes('events') || filters.type.length < 1)
      items.push(...eventsItems.filter(isIncludedOnFilters));
    if (filters.type.includes('interviews') || filters.type.length < 1)
      items.push(...interviewsItems.filter(isIncludedOnFilters));
    if (filters.type.includes('datasets') || filters.type.length < 1)
      items.push(...datasetsItems.filter(isIncludedOnFilters));

    return items;
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setMobileItems(router.query?.page * PAGE_SIZE || PAGE_SIZE);
    }
  }, [router.query.page]);

  return (
    <section className="flex justify-center items-start p-7 pb-36 md:pb-24 lg:px-28 min-w-[320px] md:space-x-8">
      <ResourcesFilters>
        {() => {
          const filters = {
            type: router.query.type?.split(',').filter(Boolean) || [],
            product: router.query.product?.split(',').filter(Boolean) || [],
            industry: router.query.industry?.split(',').filter(Boolean) || [],
            useCase: router.query.useCase?.split(',').filter(Boolean) || []
          };

          const scrollToPageStart = () => {
            document.getElementById('page').scrollIntoView({
              behavior: 'smooth',
              top: -10
            });
          };

          const allItemsSorted = getAllFilteredItems(filters).sort((a, b) => {
            a = a.props.date || '';
            b = b.props.date || '';

            return b.localeCompare(a);
          });

          const onClickSeeMore = () => {
            if (allItemsSorted.length > mobileItems) {
              setMobileItems(mobileItems + PAGE_SIZE);
            }
          };

          const pageNumber = pageQuery ? parseInt(pageQuery.toString(), 10) - 1 : 0;
          const pagesAmount = Math.ceil(allItemsSorted.length / PAGE_SIZE);

          const onToggleFilter = async (filterType, filterValue) => {
            const queryFilters = router?.query[filterType]?.split(',') ?? false;

            if (!router.query[filterType]) {
              await router.push(
                {
                  pathname: router.basePath,
                  query: { ...router.query, [filterType]: filterValue, page: '1' }
                },
                undefined,
                { scroll: false }
              );
            } else if (queryFilters.includes(filterValue)) {
              await router.push(
                {
                  pathname: router.basePath,
                  query: {
                    ...router.query,
                    [filterType]: queryFilters.filter(val => val !== filterValue).join(','),
                    page: '1'
                  }
                },
                undefined,
                { scroll: false }
              );
            } else {
              await router.push(
                {
                  pathname: router.basePath,
                  query: {
                    ...router.query,
                    [filterType]: (router.query[filterType] += `,${filterValue}`),
                    page: '1'
                  }
                },
                undefined,
                { scroll: false }
              );
            }

            scrollToPageStart();
          };

          return (
            <>
              <FilterBox
                resetFilters={() =>
                  router.push(`${router.basePath}?page=1`, undefined, { scroll: false })
                }
              >
                <div className="space-y-8">
                  <div>
                    <FilterName label="Type" />
                    <div className="space-y-4">
                      <FilterItem
                        type="type"
                        value="events"
                        label="Events"
                        counter={events.filter(event => event.slug || event.link !== '').length}
                        onClick={onToggleFilter}
                        checked={router.query?.type?.includes('events')}
                      />
                      <FilterItem
                        type="type"
                        value="blogs"
                        label="Blogs"
                        counter={blogs.length}
                        onClick={onToggleFilter}
                        checked={router.query?.type?.includes('blogs')}
                      />
                      <FilterItem
                        type="type"
                        value="resources"
                        label="Solution Briefs/White papers"
                        counter={resources.length}
                        onClick={onToggleFilter}
                        checked={router.query?.type?.includes('resources')}
                      />
                      <FilterItem
                        type="type"
                        value="caseStudies"
                        label="Case Studies"
                        counter={customers.length}
                        onClick={onToggleFilter}
                        checked={router.query?.type?.includes('caseStudies')}
                      />
                      <FilterItem
                        type="type"
                        value="datasets"
                        label="Open Datasets"
                        counter={datasets.length}
                        onClick={onToggleFilter}
                        checked={router.query?.type?.includes('datasets')}
                      />
                      <FilterItem
                        type="type"
                        value="interviews"
                        label="Interviews"
                        counter={interviews.length}
                        onClick={onToggleFilter}
                        checked={router.query?.type?.includes('interviews')}
                      />
                    </div>
                  </div>
                  <div>
                    <FilterName label="Products" />
                    <div className="space-y-4">
                      <FilterItem
                        type="product"
                        value="Scale 3D Sensor Fusion"
                        label="Scale 3D Sensor Fusion"
                        onClick={onToggleFilter}
                        checked={router.query?.product?.includes('Scale 3D Sensor Fusion')}
                      />
                      <FilterItem
                        type="product"
                        value="Scale Image"
                        label="Scale Image"
                        onClick={onToggleFilter}
                        checked={router.query?.product?.includes('Scale Image')}
                      />
                      <FilterItem
                        type="product"
                        value="Scale Video"
                        label="Scale Video"
                        onClick={onToggleFilter}
                        checked={router.query?.product?.includes('Scale Video')}
                      />
                      <FilterItem
                        type="product"
                        value="Scale Text"
                        label="Scale Text"
                        onClick={onToggleFilter}
                        checked={router.query?.product?.includes('Scale Text')}
                      />
                      <FilterItem
                        type="product"
                        value="Scale Audio"
                        label="Scale Audio"
                        onClick={onToggleFilter}
                        checked={router.query?.product?.includes('Scale Audio')}
                      />
                      <FilterItem
                        type="product"
                        value="Scale Mapping"
                        label="Scale Mapping"
                        onClick={onToggleFilter}
                        checked={router.query?.product?.includes('Scale Mapping')}
                      />
                      <FilterItem
                        type="product"
                        value="Scale Nucleus"
                        label="Scale Nucleus"
                        onClick={onToggleFilter}
                        checked={router.query?.product?.includes('Scale Nucleus')}
                      />
                      <FilterItem
                        type="product"
                        value="Scale Rapid"
                        label="Scale Rapid"
                        onClick={onToggleFilter}
                        checked={router.query?.product?.includes('Scale Rapid')}
                      />
                      <FilterItem
                        type="product"
                        value="Scale Document AI"
                        label="Scale Document AI"
                        onClick={onToggleFilter}
                        checked={router.query?.product?.includes('Scale Document AI')}
                      />
                      <FilterItem
                        type="product"
                        value="Scale Collect"
                        label="Scale Collect"
                        onClick={onToggleFilter}
                        checked={router.query?.product?.includes('Scale Collect')}
                      />
                      <FilterItem
                        type="product"
                        value="Scale Synthetic"
                        label="Scale Synthetic"
                        onClick={onToggleFilter}
                        checked={router.query?.product?.includes('Scale Synthetic')}
                      />
                    </div>
                  </div>
                  <div>
                    <FilterName label="Industry" />
                    <div className="space-y-4">
                      <FilterItem
                        type="industry"
                        value="Retail & eCommerce"
                        label="Retail & eCommerce"
                        onClick={onToggleFilter}
                        checked={router.query?.industry?.includes('Retail & eCommerce')}
                      />
                      <FilterItem
                        type="industry"
                        value="Government"
                        label="Government"
                        onClick={onToggleFilter}
                        checked={router.query?.industry?.includes('Government')}
                      />
                      <FilterItem
                        type="industry"
                        value="Real Estate"
                        label="Real Estate"
                        onClick={onToggleFilter}
                        checked={router.query?.industry?.includes('Real Estate')}
                      />
                      <FilterItem
                        type="industry"
                        value="Manufacturing"
                        label="Manufacturing"
                        onClick={onToggleFilter}
                        checked={router.query?.industry?.includes('Manufacturing')}
                      />
                      <FilterItem
                        type="industry"
                        value="Transportation"
                        label="Transportation"
                        onClick={onToggleFilter}
                        checked={router.query?.industry?.includes('Transportation')}
                      />
                      <FilterItem
                        type="industry"
                        value="Financial Services"
                        label="Financial Services"
                        onClick={onToggleFilter}
                        checked={router.query?.industry?.includes('Financial Services')}
                      />
                      <FilterItem
                        type="industry"
                        value="Forestry"
                        label="Forestry"
                        onClick={onToggleFilter}
                        checked={router.query?.industry?.includes('Forestry')}
                      />
                      <FilterItem
                        type="industry"
                        value="Food & Beverage"
                        label="Food & Beverage"
                        onClick={onToggleFilter}
                        checked={router.query?.industry?.includes('Food & Beverage')}
                      />
                    </div>
                  </div>
                  <div>
                    <FilterName label="Use Case" />
                    <div className="space-y-4">
                      <FilterItem
                        type="useCase"
                        value="Autonomous Vehicles"
                        label="Autonomous Vehicles"
                        onClick={onToggleFilter}
                        checked={router.query?.useCase?.includes('Autonomous Vehicles')}
                      />
                      <FilterItem
                        type="useCase"
                        value="Robotics"
                        label="Robotics"
                        onClick={onToggleFilter}
                        checked={router.query?.useCase?.includes('Robotics')}
                      />
                      <FilterItem
                        type="useCase"
                        value="AR/VR"
                        label="AR/VR"
                        onClick={onToggleFilter}
                        checked={router.query?.useCase?.includes('AR/VR')}
                      />
                      <FilterItem
                        type="useCase"
                        value="Document Processing"
                        label="Document Processing"
                        onClick={onToggleFilter}
                        checked={router.query?.useCase?.includes('Document Processing')}
                      />
                      <FilterItem
                        type="useCase"
                        value="Content & Language"
                        label="Content & Language"
                        onClick={onToggleFilter}
                        checked={router.query?.useCase?.includes('Content & Language')}
                      />
                      <FilterItem
                        type="useCase"
                        value="Autonomous Checkout"
                        label="Autonomous Checkout"
                        onClick={onToggleFilter}
                        checked={router.query?.useCase?.includes('Autonomous Checkout')}
                      />
                      <FilterItem
                        type="useCase"
                        value="Autonomous Drones"
                        label="Autonomous Drones"
                        onClick={onToggleFilter}
                        checked={router.query?.useCase?.includes('Autonomous Drones')}
                      />
                      <FilterItem
                        type="useCase"
                        value="Compliance"
                        label="Compliance"
                        onClick={onToggleFilter}
                        checked={router.query?.useCase?.includes('Compliance')}
                      />
                      <FilterItem
                        type="useCase"
                        value="Inventory"
                        label="Inventory"
                        onClick={onToggleFilter}
                        checked={router.query?.useCase?.includes('Inventory')}
                      />
                    </div>
                  </div>
                </div>
              </FilterBox>

              <div id="page" className="max-w-[904px] w-full">
                {allItemsSorted.length < 1 ? (
                  <div className="w-full space-y-6">
                    <p className="max-w-max mx-auto text-lg sm:text-left text-center sm:text-xl uppercase text-transform-gradient font-semibold tracking-[3px] whitespace-nowrap">
                      No resources found
                    </p>
                    <div className="mx-auto max-w-max">
                      <GradientButton
                        onClick={() => router.push(router.basePath, undefined, { scroll: false })}
                      >
                        Remove Filters
                      </GradientButton>
                    </div>
                  </div>
                ) : (
                  <Wrapper className="space-y-[72px] md:space-y-8 max-w-[904px]">
                    <div className={styles.featuredSection}>
                      {featuredResources.map(resource => resource)}
                    </div>

                    <div className="hidden md:block">
                      <div className={styles.grid}>
                        {allItemsSorted
                          .slice(pageNumber * PAGE_SIZE, pageNumber * PAGE_SIZE + PAGE_SIZE)
                          .map(box => box)}
                      </div>
                    </div>
                    <div className="md:hidden block !mb-16">
                      <div className={styles.grid}>
                        {allItemsSorted.slice(0, mobileItems).map(box => box)}
                      </div>
                    </div>

                    <div className="hidden md:block">
                      <Paginator
                        pages={pagesAmount}
                        currentPage={pageNumber}
                        isInPage
                        pageCallback={scrollToPageStart}
                      />
                    </div>

                    {mobileItems < allItemsSorted.length && (
                      <div className="block mx-auto md:hidden max-w-max">
                        <GradientButton onClick={onClickSeeMore}>See More</GradientButton>
                      </div>
                    )}
                  </Wrapper>
                )}
              </div>
            </>
          );
        }}
      </ResourcesFilters>
    </section>
  );
};

export default ResourcesList;
